import React, { useEffect, useState } from "react";

// Modules
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// App
import {
  serverUrl,
  root_auth_handshake,
  isSso,
  registrationEnabled,
} from "../../config";
import {
  auth_login,
  auth_logout,
  get_csrf_token,
} from "../../core/auth";
import { getOnlineNode } from "../../core/getNode";
import PageHeader from "../../partials/pageHeader";
import { alertMessages } from "../../partials/alertMessages";

// UI components
import {
  LockOutlined,
  PersonOutlineOutlined,
  Close,
} from "@mui/icons-material";
import {
  Stack,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
  FormHelperText,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Snackbar,
  IconButton,
} from "@mui/material";
import { Col, Row } from "react-bootstrap";

const CSRF_URL = `${serverUrl}/session/token`;
const REGISTER_URL = `${serverUrl}/user/register?_format=json`;

export default function Login(props) {
  const [isAllowed, setAllowed] = useState(isSso ? false : true);
  const [isLoading, setLoading] = useState(false); // login spinner
  const [isError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Registration dialog state
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [values, setValues] = useState({
    email: "",
    password: "",
    confirm: "",
    firstName: "",
    lastName: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const isInModal = props.type === "modal";

  /** redux */
  const user = useSelector((state) => state).authReducer.user;

  /** hooks */
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // check session
  useEffect(() => {
    if (user.current_user && user.current_user.uid) {
      getOnlineNode(`user/${user.current_user.uid}`)
        .then(() => {
          setLoading(false);
          navigate("/");
        })
        .catch(() => {
          setLoading(false);
          navigate("/");
        });
    }
    // eslint-disable-next-line
  }, []);

  const doLogin = async (event, retry = false) => {
    event.preventDefault();

    if (!username) {
      setError(true);
      setErrorMessage(alertMessages.invalidUsername.title);
      return;
    }
    if (!password) {
      setError(true);
      setErrorMessage(alertMessages.invalidPassword.title);
      return;
    }

    setLoading(true);

    try {
      const csrf = (await get_csrf_token()).data;
      const auth_response = await auth_login(username, password, csrf);

      setLoading(false);
      setError(false);
      dispatch({ type: "SAVE_SESSION", payload: auth_response.data });
    } catch (auth_error) {
      // Drupal returns 403 when a stale session exists
      if (auth_error.response?.status === 403 && !retry) {
        try {
          await auth_logout();

          return doLogin({ preventDefault() {} }, true);
        } catch (logout_error) {
          setLoading(false);
          setError(true);
          setErrorMessage("Logout failed.");
        }
      }

      let message = "Unknown error!";
      if (auth_error?.response?.data?.message) {
        message = auth_error.response.data.message;
      }

      setLoading(false);
      setError(true);
      setErrorMessage(message);
    }
  };

  if (!isAllowed) {
    return (
      <main className={`login screen d-flex flex-column align-items-center`}>
        <Row className={`g-0 w-50`}>
          <PageHeader pageName="Root Auth" filters={false} />
        </Row>

        <Row className={`g-0 w-50`}>
          <Col xs={12}>
            <form>
              <div className="form-group mt-1">
                <Row>
                  <Col>
                    <FormControl sx={{ width: 1 }}>
                      <InputLabel
                        htmlFor="rootAuth"
                        sx={{ backgroundColor: "#fff" }}
                      >
                        <Typography
                          sx={{ color: "primary.main", fontSize: "16px" }}
                        >
                          <LockOutlined
                            sx={{ paddingRight: "5px", height: "25px" }}
                          />
                          Auth
                        </Typography>
                      </InputLabel>
                      <OutlinedInput
                        type="password"
                        id={"rootAuth"}
                        onInput={(e) => {
                          if (e.target.value === root_auth_handshake) {
                            setAllowed(true);
                          }
                        }}
                      />
                    </FormControl>
                  </Col>
                </Row>
              </div>
            </form>
          </Col>
        </Row>
      </main>
    );
  }

  const onChange = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((err) => ({ ...err, [k]: "" }));
    setServerError(null);
  };

  const validate = () => {
    const e = {};
    if (!values.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      e.email = "Invalid email";
    if (!values.password) e.password = "Required";
    else if (values.password.length < 8) e.password = "Min 8 characters";
    if (!values.confirm) e.confirm = "Required";
    else if (values.confirm !== values.password)
      e.confirm = "Passwords do not match";
    if (!values.firstName.trim()) e.firstName = "Required";
    if (!values.lastName.trim()) e.lastName = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setRegistrationLoading(true);
    setServerError(null);
    try {
      const tokenRes = await fetch(CSRF_URL, { credentials: "include" });
      if (!tokenRes.ok) throw new Error("Failed to fetch CSRF token");
      const csrfToken = await tokenRes.text();

      const payload = {
        name: [{ value: values.email }],
        mail: [{ value: values.email }],
        pass: [{ value: values.password }],
        field_first_name: [{ value: values.firstName }],
        field_last_name: [{ value: values.lastName }],
      };

      const res = await fetch(REGISTER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Registration failed (${res.status})`);
      }

      setRegistrationLoading(false);
      setOpen(false);
      setShowSuccess(true);
      setValues({
        email: "",
        password: "",
        confirm: "",
        firstName: "",
        lastName: "",
      });
    } catch (err) {
      try {
        const errJson = JSON.parse(err.message);
        if (errJson?.message) {
          setServerError(errJson);
        } else if (errJson?.name?.[0]?.messages?.[0]?.message) {
          setServerError({ message: errJson.name[0].messages[0].message });
        } else if (errJson?.mail?.[0]?.messages?.[0]?.message) {
          setServerError({ message: errJson.mail[0].messages[0].message });
        } else {
          setServerError({ message: "Registration failed" });
        }
      } catch {
        setServerError({ message: err.message || "Registration failed" });
      }
      // setServerError(err.message);

      setRegistrationLoading(false);
    }
  };

  const disabled =
    registrationLoading ||
    !values.email ||
    !values.password ||
    !values.confirm ||
    !values.firstName ||
    !values.lastName;

  return (
    <main
      className={`login ${
        isInModal ? "mt-5" : "screen d-flex flex-column align-items-center"
      }`}
    >
      <Row className={`g-0 ${isInModal ? "" : "w-50 mb-3"}`}>
        {!isInModal && <PageHeader pageName="Login" filters={false} />}
      </Row>

      <Row className={`g-0 ${isInModal ? "" : "w-50"}`}>
        <Col xs={12}></Col>

        {isError && (
          <>
            {errorMessage === "token" && (
              <Col xs={12} className="mb-4">
                <Alert variant="filled" severity="error">
                  Failed to fetch token. Please try again or contact an
                  administrator!
                </Alert>
              </Col>
            )}

            {errorMessage !== "token" &&
              errorMessage !== "username" &&
              errorMessage !== "password" && (
                <Col xs={12} className="mb-4">
                  <Alert variant="filled" severity="error">
                    {errorMessage}
                  </Alert>
                </Col>
              )}
          </>
        )}

        <Col xs={12}>
          <form onSubmit={doLogin}>
            <div className="form-group">
              <Row>
                <Col>
                  <FormControl
                    error={
                      isError &&
                      errorMessage === alertMessages.invalidUsername.title
                    }
                    sx={{ width: 1 }}
                  >
                    <InputLabel
                      htmlFor="username"
                      sx={{ backgroundColor: "#fff" }}
                    >
                      <Typography
                        sx={{ color: "primary.main", fontSize: "16px" }}
                      >
                        <PersonOutlineOutlined
                          sx={{ paddingRight: "5px", height: "25px" }}
                        />
                        Username
                      </Typography>
                    </InputLabel>
                    <OutlinedInput
                      id={"username"}
                      value={username}
                      onInput={(e) => {
                        setUsername(e.target.value);
                        setError(false);
                        setErrorMessage("");
                      }}
                    />
                    {isError &&
                      errorMessage === alertMessages.invalidUsername.title && (
                        <FormHelperText
                          sx={{ marginLeft: 0 }}
                          id="username-text"
                        >
                          {errorMessage}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Col>
              </Row>
            </div>

            <div className="form-group mt-3">
              <Row>
                <Col>
                  <FormControl
                    error={
                      isError &&
                      errorMessage === alertMessages.invalidPassword.title
                    }
                    sx={{ width: 1 }}
                  >
                    <InputLabel
                      htmlFor="password"
                      sx={{ backgroundColor: "#fff" }}
                    >
                      <Typography
                        sx={{ color: "primary.main", fontSize: "16px" }}
                      >
                        <LockOutlined
                          sx={{ paddingRight: "5px", height: "25px" }}
                        />
                        Password
                      </Typography>
                    </InputLabel>
                    <OutlinedInput
                      type="password"
                      id={"password"}
                      value={password}
                      onInput={(e) => {
                        setPassword(e.target.value);
                        setError(false);
                        setErrorMessage("");
                      }}
                    />
                    {isError &&
                      errorMessage === alertMessages.invalidPassword.title && (
                        <FormHelperText
                          sx={{ marginLeft: 0 }}
                          id="password-text"
                        >
                          {errorMessage}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Col>
              </Row>
            </div>

            <div className="form-group mt-3">
              <Row>
                <Col className="d-flex justify-content-center">
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={2}
                      sx={{ width: "100%" }}
                    >
                      {registrationEnabled && (
                        <Button
                          sx={{ width: "50%" }}
                          variant="outlined"
                          onClick={() => setOpen(true)}
                        >
                          Register
                        </Button>
                      )}
                      <Button
                        sx={{ width: "50%" }}
                        variant="contained"
                        type="submit"
                      >
                        Login
                      </Button>
                    </Stack>
                  )}
                </Col>
              </Row>
            </div>
          </form>
        </Col>
      </Row>

      <Dialog
        open={open}
        onClose={() => {
          if (!registrationLoading) {
            setOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create account</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {serverError && (
              <Alert severity="error">
                {serverError.message ?? "Unknown error"}
              </Alert>
            )}
            <TextField
              label="Email"
              type="email"
              value={values.email}
              onChange={onChange("email")}
              error={!!errors.email}
              helperText={errors.email || " "}
              required
              fullWidth
              size="small"
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="First name"
                value={values.firstName}
                onChange={onChange("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName || " "}
                required
                fullWidth
                size="small"
              />
              <TextField
                label="Last name"
                value={values.lastName}
                onChange={onChange("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName || " "}
                required
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Password"
              type="password"
              value={values.password}
              onChange={onChange("password")}
              error={!!errors.password}
              helperText={errors.password || "Min 8 characters"}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Confirm password"
              type="password"
              value={values.confirm}
              onChange={onChange("confirm")}
              error={!!errors.confirm}
              helperText={errors.confirm || " "}
              required
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ mr: 2, mb: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            disabled={registrationLoading}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Box position="relative">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={disabled}
            >
              Create account
            </Button>
            {registrationLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  mt: "-12px",
                  ml: "-12px",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => {
          setShowSuccess(false);
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setShowSuccess(false);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <Alert
          onClose={() => {
            setShowSuccess(false);
          }}
          severity="success"
          variant="filled"
          sx={{ width: "90%", marginTop: "90px", textAlign: "center" }}
        >
          Registration successful. Your account is now awaiting approval. You
          will be notified by email once it has been activated.
        </Alert>
      </Snackbar>
    </main>
  );
}
